<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Background overlay -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="$emit('close')"></div>
    
    <!-- Modal container -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl transform transition-all">
        <!-- Modal header -->
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900 flex items-center">
              <svg class="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0v1a1 1 0 001 1h8a1 1 0 001-1V4M7 4H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2h-2"/>
              </svg>
              Add Social Media Links
            </h3>
            <button 
              @click="$emit('close')"
              class="text-gray-400 hover:text-gray-600 transition-colors">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Modal content -->
        <div class="px-6 py-6">
          <p class="text-gray-600 mb-6">Add social media links to connect with your customers. These will appear as professional circular buttons in your email template.</p>
          
          <!-- Social media inputs -->
          <div class="space-y-4">
            <!-- Facebook -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
              </div>
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                <input 
                  v-model="socialLinks.facebook" 
                  type="url" 
                  placeholder="https://facebook.com/yourpage"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
            </div>

            <!-- Instagram -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-gradient-to-br from-blue-600 via-pink-600 to-orange-600 rounded-full flex items-center justify-center">
                  <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.864 3.708 13.713 3.708 12.416s.49-2.448 1.418-3.323C6.001 8.218 7.152 7.728 8.449 7.728s2.448.49 3.323 1.365c.875.875 1.365 2.026 1.365 3.323s-.49 2.448-1.365 3.323c-.875.875-2.026 1.349-3.323 1.349zm7.078 0c-1.297 0-2.448-.49-3.323-1.297-.875-.875-1.365-2.026-1.365-3.323s.49-2.448 1.365-3.323c.875-.875 2.026-1.365 3.323-1.365s2.448.49 3.323 1.365c.875.875 1.365 2.026 1.365 3.323s-.49 2.448-1.365 3.323c-.875.875-2.026 1.349-3.323 1.349z"/>
                  </svg>
                </div>
              </div>
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                <input 
                  v-model="socialLinks.instagram" 
                  type="url" 
                  placeholder="https://instagram.com/yourprofile"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent">
              </div>
            </div>

            <!-- LinkedIn -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center">
                  <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </div>
              </div>
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                <input 
                  v-model="socialLinks.linkedin" 
                  type="url" 
                  placeholder="https://linkedin.com/company/yourcompany"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent">
              </div>
            </div>

            <!-- Twitter -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                  <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </div>
              </div>
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
                <input 
                  v-model="socialLinks.twitter" 
                  type="url" 
                  placeholder="https://twitter.com/yourhandle"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent">
              </div>
            </div>

            <!-- YouTube -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                  <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </div>
              </div>
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">YouTube</label>
                <input 
                  v-model="socialLinks.youtube" 
                  type="url" 
                  placeholder="https://youtube.com/yourchannel"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent">
              </div>
            </div>

            <!-- TikTok -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-black rounded-full flex items-center justify-center">
                  <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                </div>
              </div>
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">TikTok</label>
                <input 
                  v-model="socialLinks.tiktok" 
                  type="url" 
                  placeholder="https://tiktok.com/@yourhandle"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent">
              </div>
            </div>
          </div>

          <!-- Style Options -->
          <div class="mt-8 border-t border-gray-200 pt-6">
            <h4 class="text-sm font-semibold text-gray-900 mb-4">Style Options</h4>
            
            <div class="grid grid-cols-2 gap-4">
              <!-- Button Size -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Button Size</label>
                <select 
                  v-model="socialStyle.size" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="32">Small (32px)</option>
                  <option value="40">Medium (40px)</option>
                  <option value="48">Large (48px)</option>
                  <option value="56">Extra Large (56px)</option>
                </select>
              </div>

              <!-- Button Style -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Button Style</label>
                <select 
                  v-model="socialStyle.style" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="circle">Circular</option>
                  <option value="rounded">Rounded Square</option>
                  <option value="square">Square</option>
                </select>
              </div>

              <!-- Alignment -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Alignment</label>
                <select 
                  v-model="socialStyle.alignment" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="center">Center</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
              </div>

              <!-- Spacing -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Spacing</label>
                <select 
                  v-model="socialStyle.spacing" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="5">Tight (5px)</option>
                  <option value="10">Normal (10px)</option>
                  <option value="15">Loose (15px)</option>
                  <option value="20">Extra Loose (20px)</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Preview -->
          <div class="mt-6 border-t border-gray-200 pt-6">
            <h4 class="text-sm font-semibold text-gray-900 mb-4">Preview</h4>
            <div 
              class="bg-gray-50 p-6 rounded-lg border border-gray-200"
              :style="{ textAlign: socialStyle.alignment }">
              <div class="inline-flex items-center space-x-2" :style="{ gap: socialStyle.spacing + 'px' }">
                <!-- Facebook Preview -->
                <a 
                  v-if="socialLinks.facebook" 
                  :href="socialLinks.facebook"
                  class="inline-flex items-center justify-center bg-blue-600 text-white font-bold text-decoration-none"
                  :style="getButtonStyle()">
                  f
                </a>
                
                <!-- Instagram Preview -->
                <a 
                  v-if="socialLinks.instagram" 
                  :href="socialLinks.instagram"
                  class="inline-flex items-center justify-center text-white font-bold text-decoration-none"
                  :style="{ ...getButtonStyle(), background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)' }">
                  📷
                </a>
                
                <!-- LinkedIn Preview -->
                <a 
                  v-if="socialLinks.linkedin" 
                  :href="socialLinks.linkedin"
                  class="inline-flex items-center justify-center bg-blue-700 text-white font-bold text-decoration-none"
                  :style="getButtonStyle()">
                  in
                </a>
                
                <!-- Twitter Preview -->
                <a 
                  v-if="socialLinks.twitter" 
                  :href="socialLinks.twitter"
                  class="inline-flex items-center justify-center bg-blue-400 text-white font-bold text-decoration-none"
                  :style="getButtonStyle()">
                  🐦
                </a>
                
                <!-- YouTube Preview -->
                <a 
                  v-if="socialLinks.youtube" 
                  :href="socialLinks.youtube"
                  class="inline-flex items-center justify-center bg-red-600 text-white font-bold text-decoration-none"
                  :style="getButtonStyle()">
                  ▶
                </a>
                
                <!-- TikTok Preview -->
                <a 
                  v-if="socialLinks.tiktok" 
                  :href="socialLinks.tiktok"
                  class="inline-flex items-center justify-center bg-black text-white font-bold text-decoration-none"
                  :style="getButtonStyle()">
                  TT
                </a>
              </div>
              <p v-if="!hasAnySocialLinks" class="text-gray-500 text-sm italic">Add social media links above to see preview</p>
            </div>
          </div>
        </div>

        <!-- Modal footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button 
            @click="$emit('close')"
            class="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            Cancel
          </button>
          <button 
            @click="addSocialComponent"
            :disabled="!hasAnySocialLinks"
            :class="[
              'px-6 py-2 rounded-lg font-medium transition-colors',
              hasAnySocialLinks 
                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            ]">
            Add Social Links
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SocialsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    initialData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      socialLinks: {
        facebook: '',
        instagram: '',
        linkedin: '',
        twitter: '',
        youtube: '',
        tiktok: ''
      },
      socialStyle: {
        size: '40',
        style: 'circle',
        alignment: 'center',
        spacing: '10'
      }
    }
  },
  computed: {
    hasAnySocialLinks() {
      return Object.values(this.socialLinks).some(link => link.trim())
    }
  },
  methods: {
    getButtonStyle() {
      const borderRadius = {
        circle: '50%',
        rounded: '8px',
        square: '0px'
      }
      
      return {
        width: this.socialStyle.size + 'px',
        height: this.socialStyle.size + 'px',
        borderRadius: borderRadius[this.socialStyle.style],
        fontSize: Math.floor(this.socialStyle.size * 0.4) + 'px',
        lineHeight: this.socialStyle.size + 'px'
      }
    },
    
    addSocialComponent() {
      const socialData = {
        links: { ...this.socialLinks },
        style: { ...this.socialStyle }
      }
      
      this.$emit('add-social', socialData)
      this.$emit('close')
    }
  },
  
  watch: {
    show(newValue) {
      if (newValue && this.initialData) {
        // Pre-populate with initial data if provided
        this.socialLinks = { ...this.socialLinks, ...this.initialData.links }
        this.socialStyle = { ...this.socialStyle, ...this.initialData.style }
      }
    }
  }
}
</script>

<style scoped>
/* Additional styling for the modal */
.fixed {
  position: fixed;
}

/* Smooth transitions */
* {
  transition: all 0.2s ease;
}

/* Focus styles */
input:focus, select:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Button hover effects */
button:hover {
  transform: translateY(-1px);
}

button:disabled:hover {
  transform: none;
}
</style>
